import { createSlice } from "@reduxjs/toolkit";
import { EEstadoTareaOperacion } from "domain/types";

const initialState = {
  isLoading: false,
  submitIsLoading: false,
  error: false,
  tareasIds: [],
  tareasById: {},
  solicitud: {},
};

const tareasSolicitudSlice = createSlice({
  name: "tareasSolicitud",
  initialState,
  reducers: {
    submitTareaFileInited: (state) => {
      state.submitIsLoading = true;
    },
    submitTareaFileCompleted: (state) => {
      state.submitIsLoading = false;
    },
    submitTareaFileFailed: (state) => {
      state.submitIsLoading = false;
    },
    fetchSolicitudInited: (state) => {
      state.isLoading = true;
    },
    fetchSolicitudCompleted: (state, action) => {
      const buscarPor = (tipoRelacion) =>
        action.payload.personas.find((p) => p.tipoRelacion === tipoRelacion);
      const titular = buscarPor("Titular");

      state.solicitud = {
        nombresTitular: titular.nombre,
        apellidosTitular: titular.apellido,
        importeSolicitado: action.payload.importeNetoAPercibir,
        situacionOp: action.payload.situacion,
        conyuge: buscarPor("Conyuge"),
        codeudor: buscarPor("Codeudor"),
        conyugeCodeudor: buscarPor("Conyuge_del_Codeudor"),
      };
      state.isLoading = false;
      state.error = false;
    },
    fetchSolicitudFailed: (state) => {
      state.isLoading = false;
      state.error = true;
    },
    fetchTareasInited: (state) => {
      state.tareasIds = [];
      state.tareasById = {};
      state.isLoading = true;
    },
    fetchTareasCompleted: (state, action) => {
      state.isLoading = false;
      state.tareasIds = action.payload.map((tarea) => tarea.tareaId);
      state.tareasById = action.payload.reduce((map, tarea) => {
        map[tarea.tareaId] = { ...tarea, tempFiles: [] };
        return map;
      }, {});
      state.error = false;
    },
    fetchTareasFailed: (state) => {
      state.isLoading = false;
      state.error = true;
    },
    archivoDownloaded: (state, action) => {
      const tarea = state.tareasById[action.payload.tareaId];
      if (!tarea) return;

      const idx = tarea.archivos.findIndex((a) => a.id === action.payload.id);
      const archivoActualizado = { ...action.payload, loaded: true };
      if (idx >= 0) {
        tarea.archivos[idx] = archivoActualizado;
      } else {
        tarea.archivos.push(archivoActualizado);
      }
    },
    archivoTareasUploadCompleted: (state, action) => {
      const tarea = state.tareasById[action.payload.tareaId];
      if (!tarea) return;
      tarea.tempFiles.push({
        fileName: action.payload.filename,
        serverId: action.payload.serverId,
      });
    },
    archivoTareasRemoved: (state, action) => {
      const tarea = state.tareasById[action.payload.tareaId];
      if (!tarea) return;
      tarea.tempFiles = tarea.tempFiles.filter(
        (t) => t.fileName !== action.payload.filename,
      );
    },
    tareaEnviadaParaAnalizar: (state, action) => {
      state.tareasById[action.payload.tareaId] = action.payload;
    },
  },
});

export const {
  fetchSolicitudInited,
  fetchSolicitudFailed,
  fetchSolicitudCompleted,
  fetchTareasInited,
  fetchTareasCompleted,
  fetchTareasFailed,
  archivoDownloaded,
  archivoTareasUploadCompleted,
  archivoTareasRemoved,
  submitTareaFileInited,
  submitTareaFileCompleted,
  submitTareaFileFailed,
  tareaEnviadaParaAnalizar,
} = tareasSolicitudSlice.actions;

export function fetchTareas(solicitudId) {
  return async (dispatch, getState, services) => {
    try {
      dispatch(fetchTareasInited());
      const response = await services.api.getTareasSolicitud(solicitudId);

      const tareas = response
        .filter(
          (tarea) =>
            tarea.estado !== EEstadoTareaOperacion.Excepcionada &&
            !tarea.interna,
        )
        .map((tarea) => {
          var archivos = tarea.archivos.map((a) => {
            let extraFields = undefined;
            if (a.url) {
              let fileName = a.url.split("/").pop().split(".")[0];
              extraFields = {
                fileName,
                id: a.id ? a.id : fileName.toLowerCase(),
                name: fileName,
                loaded: true,
              };
            }

            return {
              ...a,
              tareaId: tarea.tareaId,
              ...extraFields,
            };
          });

          return { ...tarea, archivos };
        });

      const tareasOk = tareas.filter(
        (tarea) => tarea.estado === EEstadoTareaOperacion.OK,
      );
      const tareasEnAnalisis = tareas.filter(
        (tarea) => tarea.estado === EEstadoTareaOperacion.Para_Analizar,
      );
      const tareasPendientes = tareas.filter(
        (t) =>
          t.estado !== EEstadoTareaOperacion.OK &&
          t.estado !== EEstadoTareaOperacion.Para_Analizar,
      );
      const finalTareas = tareasPendientes
        .concat(tareasEnAnalisis)
        .concat(tareasOk);

      dispatch(fetchTareasCompleted(finalTareas));
    } catch (error) {
      dispatch(services.errorHandler.checkErrorAction(error));
      dispatch(fetchTareasFailed(error));
    }
  };
}

export const fetchSolicitud = (solicitudId) => {
  return async (dispatch, getState, services) => {
    try {
      dispatch(fetchSolicitudInited());
      const response = await services.api.getSolicitudById(solicitudId);
      dispatch(fetchSolicitudCompleted(response));
    } catch (error) {
      dispatch(fetchSolicitudFailed(error));
    }
  };
};

export const uploadArchivoInited = ({
  fieldName,
  file,
  metadata,
  load,
  error,
  progress,
  abort,
}) => {
  return async (dispatch, getState, services) => {
    const directory = "temp";
    const fileService = services.fileService(directory);
    try {
      await fileService.upload(file, load, progress);
    } catch (e) {
      console.error(e);
      error(e.message);
      services.errorHandler.logRemoteError(
        e,
        uploadArchivoInited,
        [fieldName, file, metadata, load, error, progress, abort],
        getState(),
      );
      dispatch(services.errorHandler.checkErrorAction(e));
    }
  };
};

export const removeArchivoInited = ({ serverId, load, error }) => {
  return async (dispatch, getState, services) => {
    const directory = "temp";
    const fileService = services.fileService(directory);
    try {
      load();
      await fileService.delete(serverId);
    } catch (e) {
      console.error(e);
      error(e);
      services.errorHandler.logRemoteError(
        e,
        removeArchivoInited,
        [serverId, load, error],
        getState(),
      );
      dispatch(services.errorHandler.checkErrorAction(e));
    }
  };
};

export const downloadArchivoInited = (solicitudId, tarea, fileName, fileId) => {
  return async (dispatch, getState, services) => {
    const directory = `solicitudes/${solicitudId}/tareas/${tarea.sequence}/archivos`;
    const fileService = services.fileService(directory);
    try {
      const response = await fileService.download(
        encodeURIComponent(fileName.toLowerCase()).replace(/%2520/g, "%20"),
      );

      dispatch(
        archivoDownloaded({
          tareaId: tarea.tareaId,
          id: fileId ? fileId : fileName.toLowerCase(),
          url: response.url,
          name: fileName,
          extension: fileName.split(".").pop(),
        }),
      );
    } catch (e) {
      console.error(e);
      dispatch(services.errorHandler.checkErrorAction(e));
    }
  };
};

export const submitTareaFile = (solicitudId, tarea, comentario) => {
  return async (dispatch, getState, services) => {
    try {
      let tareaUpdated = JSON.parse(JSON.stringify(tarea));
      if (comentario) {
        tareaUpdated.log.push({ comentario: comentario, usrExterno: true });
      }

      const form = {
        Comentario: comentario,
        data: "",
        solicitudId: solicitudId,
        tareaId: tarea.tareaId,
        sequence: tarea.sequence,
        archivos: tarea.tempFiles,
      };

      dispatch(submitTareaFileInited());
      const response = await services.api.postTarea(
        solicitudId,
        tarea.sequence,
        form,
      );
      response.urls.map((url) => {
        const fileName = decodeURI(url.split("/").pop());
        dispatch(downloadArchivoInited(solicitudId, tarea, fileName));
      });

      // Originación decide el estado: la tarea 324 sigue "Pendiente" mientras haya una
      // firma digital en curso (rendezvous con la subida del concesionario).
      tareaUpdated.estado = response.estado ?? EEstadoTareaOperacion.Para_Analizar;
      tareaUpdated.tempFiles = [];

      dispatch(tareaEnviadaParaAnalizar(tareaUpdated));
      dispatch(submitTareaFileCompleted());
    } catch (error) {
      console.error(error);
      dispatch(submitTareaFileFailed(error));
    }
  };
};

export default tareasSolicitudSlice.reducer;
